import { useEffect, useMemo, useState } from 'react';
import {
  simular,
  criarCheckout,
  formatBRL,
  type Simulacao,
  type OpcaoParcela,
  type Produto,
  type CheckoutResult,
} from '../lib/api';
import { tokenizeCard } from './tokenize';

export interface Customizacao {
  nomeNaCamisa: string;
  tamanho: string;
  posicao: 'BACK' | 'CHEST';
  cor: string;
  svgSnapshot: string;
}

interface Props {
  produto: Produto;
  customizacao: Customizacao;
  onVoltar: () => void;
}

type Metodo = 'PIX' | 'CREDIT_CARD';

export function CheckoutView({ produto, customizacao, onVoltar }: Props) {
  const valor = produto.precoCentavos;
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [cpf, setCpf] = useState('');
  const [metodo, setMetodo] = useState<Metodo>('PIX');
  const [sim, setSim] = useState<Simulacao | null>(null);
  const [parcelas, setParcelas] = useState(1);
  // cartão (demo — nunca enviado ao backend; em produção o SDK do gateway tokeniza)
  const [cartaoNumero, setCartaoNumero] = useState('');
  const [cartaoNome, setCartaoNome] = useState('');
  const [cartaoValidade, setCartaoValidade] = useState('');
  const [cartaoCvv, setCartaoCvv] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [resultado, setResultado] = useState<CheckoutResult | null>(null);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    simular(valor).then(setSim).catch(() => setErro('Não consegui calcular o parcelamento.'));
  }, [valor]);

  const opcaoSel: OpcaoParcela | undefined = useMemo(
    () => sim?.opcoes.find((o) => o.parcelas === parcelas),
    [sim, parcelas],
  );

  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
  const formOk = nome.trim().length >= 2 && emailOk && telefone.replace(/\D/g, '').length >= 10;
  const cartaoOk =
    metodo !== 'CREDIT_CARD' ||
    (cartaoNumero.replace(/\D/g, '').length >= 13 && cartaoNome.trim().length >= 2 && /\d{2}\/\d{2}/.test(cartaoValidade) && cartaoCvv.length >= 3);

  async function pagar() {
    setErro('');
    if (!formOk) return setErro('Confira seus dados de contato.');
    if (!cartaoOk) return setErro('Confira os dados do cartão.');
    setEnviando(true);
    try {
      const telefoneE164 = '+55' + telefone.replace(/\D/g, '');
      const base = {
        customer: { nome: nome.trim(), email: email.trim(), telefone: telefoneE164, ...(cpf ? { cpf: cpf.replace(/\D/g, '') } : {}) },
        items: [{ productId: produto.id, qtd: 1, customization: customizacao }],
      };
      let input;
      if (metodo === 'PIX') {
        input = { ...base, metodo: 'PIX' as const };
      } else {
        // TOKENIZAÇÃO NO CLIENT — em produção: Asaas SDK (backend nunca vê PAN/CVV).
        const cardToken = await tokenizeCard({ numero: cartaoNumero, nome: cartaoNome, validade: cartaoValidade, cvv: cartaoCvv });
        input = { ...base, metodo: 'CREDIT_CARD' as const, parcelas, cardToken };
      }
      const r = await criarCheckout(input);
      setResultado(r);
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setEnviando(false);
    }
  }

  if (resultado) return <Resultado r={resultado} onNovo={onVoltar} copiado={copiado} setCopiado={setCopiado} />;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8">
      <button onClick={onVoltar} className="mb-4 text-sm text-dourado hover:underline">← voltar e editar a camisa</button>
      <h1 className="font-display text-3xl font-black">Finalizar pedido</h1>

      <div className="mt-6 grid gap-8 md:grid-cols-[1fr_1.1fr]">
        {/* RESUMO */}
        <aside className="rounded-2xl border border-dourado/20 bg-black/30 p-4">
          <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-amber-100/70">Seu pedido</h2>
          <div className="rounded-xl bg-black/40 p-2" dangerouslySetInnerHTML={{ __html: customizacao.svgSnapshot }} />
          <dl className="mt-4 space-y-1 text-sm">
            <div className="flex justify-between"><dt className="text-amber-100/60">Produto</dt><dd>{produto.nome}</dd></div>
            <div className="flex justify-between"><dt className="text-amber-100/60">Nome na camisa</dt><dd className="font-bold">{customizacao.nomeNaCamisa.toUpperCase()}</dd></div>
            <div className="flex justify-between"><dt className="text-amber-100/60">Tamanho</dt><dd>{customizacao.tamanho}</dd></div>
            <div className="flex justify-between"><dt className="text-amber-100/60">Posição</dt><dd>{customizacao.posicao === 'BACK' ? 'Costas' : 'Peito'}</dd></div>
            <div className="mt-2 flex justify-between border-t border-white/10 pt-2 text-base"><dt>Total à vista</dt><dd className="font-black text-dourado">{formatBRL(valor)}</dd></div>
          </dl>
        </aside>

        {/* PAGAMENTO */}
        <section className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-2">
            <Campo id="nome" label="Nome completo" value={nome} onChange={setNome} placeholder="Seu nome" />
            <Campo id="email" label="E-mail" value={email} onChange={setEmail} placeholder="voce@email.com" type="email" />
            <Campo id="tel" label="Celular (WhatsApp)" value={telefone} onChange={setTelefone} placeholder="(63) 99999-0000" />
            <Campo id="cpf" label="CPF (opcional)" value={cpf} onChange={setCpf} placeholder="000.000.000-00" />
          </div>

          <div>
            <span className="mb-1 block text-xs font-bold uppercase tracking-wider text-amber-100/70">Forma de pagamento</span>
            <div className="flex gap-2">
              {(['PIX', 'CREDIT_CARD'] as Metodo[]).map((m) => (
                <button key={m} type="button" aria-pressed={metodo === m} onClick={() => setMetodo(m)}
                  className={`flex-1 rounded-full px-4 py-2 text-sm font-bold transition ${metodo === m ? 'bg-dourado text-[#241202]' : 'border border-dourado/25 text-amber-100/80'}`}>
                  {m === 'PIX' ? 'PIX à vista' : 'Cartão parcelado'}
                </button>
              ))}
            </div>
          </div>

          {metodo === 'CREDIT_CARD' && (
            <div className="space-y-3">
              <div>
                <label htmlFor="parcelas" className="mb-1 block text-xs font-bold uppercase tracking-wider text-amber-100/70">Parcelas</label>
                <select id="parcelas" value={parcelas} onChange={(e) => setParcelas(Number(e.target.value))}
                  className="w-full rounded-xl border border-dourado/25 bg-black/40 px-4 py-3 outline-none focus:border-laranja">
                  {sim?.opcoes.map((o) => (
                    <option key={o.parcelas} value={o.parcelas}>{o.descricao}</option>
                  ))}
                </select>
                {opcaoSel && (
                  <p className="mt-1 text-xs text-amber-100/60">
                    {opcaoSel.parcelas}x de <b>{formatBRL(opcaoSel.valorParcelaCentavos)}</b> — total {formatBRL(opcaoSel.valorTotalCentavos)}
                    {opcaoSel.parcelas > 1 && ` (juros ${opcaoSel.jurosEfetivoMensalPercent}% a.m., embutido)`}
                  </p>
                )}
              </div>
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200/80">
                ⚠️ Ambiente de demonstração: <b>não digite um cartão real</b>. Nenhum dado de cartão é enviado ao servidor —
                em produção o SDK do gateway (Asaas) tokeniza aqui no navegador.
              </div>
              <Campo id="cnum" label="Número do cartão" value={cartaoNumero} onChange={(v) => setCartaoNumero(v)} placeholder="4111 1111 1111 1111" />
              <Campo id="cnome" label="Nome impresso" value={cartaoNome} onChange={setCartaoNome} placeholder="COMO ESTÁ NO CARTÃO" />
              <div className="grid grid-cols-2 gap-3">
                <Campo id="cval" label="Validade" value={cartaoValidade} onChange={setCartaoValidade} placeholder="MM/AA" />
                <Campo id="ccvv" label="CVV" value={cartaoCvv} onChange={setCartaoCvv} placeholder="123" />
              </div>
            </div>
          )}

          {erro && <p className="text-sm text-red-400" role="alert">{erro}</p>}

          <button onClick={pagar} disabled={enviando}
            className="w-full rounded-full bg-gradient-to-r from-dourado to-laranja px-5 py-3 font-extrabold text-[#241202] transition enabled:hover:brightness-105 disabled:opacity-50">
            {enviando ? 'Processando…' : metodo === 'PIX' ? 'Gerar PIX' : `Pagar ${opcaoSel ? formatBRL(opcaoSel.valorTotalCentavos) : ''}`}
          </button>
        </section>
      </div>
    </div>
  );
}

function Campo({ id, label, value, onChange, placeholder, type = 'text' }: {
  id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-bold uppercase tracking-wider text-amber-100/70">{label}</label>
      <input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-xl border border-dourado/25 bg-black/40 px-4 py-3 outline-none focus:border-laranja" />
    </div>
  );
}

function Resultado({ r, onNovo, copiado, setCopiado }: { r: CheckoutResult; onNovo: () => void; copiado: boolean; setCopiado: (b: boolean) => void }) {
  const pago = r.status === 'CONFIRMED' || r.status === 'RECEIVED';
  return (
    <div className="mx-auto max-w-xl px-4 py-10 text-center">
      {r.metodo === 'PIX' ? (
        <>
          <h1 className="font-display text-3xl font-black text-dourado">Quase lá! 🐊</h1>
          <p className="mt-2 text-amber-100/80">Pague o PIX abaixo. Assim que a confirmação chegar, seu pedido entra para produção.</p>
          <div className="mx-auto mt-6 w-48 rounded-2xl bg-white p-3">
            <img src={r.pixQrCode} alt="QR Code PIX" className="w-full" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <div className="mt-4 text-left">
            <span className="text-xs font-bold uppercase tracking-wider text-amber-100/70">PIX copia e cola</span>
            <div className="mt-1 flex gap-2">
              <input readOnly value={r.pixCopiaECola ?? ''} className="w-full truncate rounded-xl border border-dourado/25 bg-black/40 px-3 py-2 text-sm" />
              <button onClick={() => { navigator.clipboard?.writeText(r.pixCopiaECola ?? ''); setCopiado(true); setTimeout(() => setCopiado(false), 1500); }}
                className="rounded-xl bg-dourado px-4 py-2 text-sm font-bold text-[#241202]">{copiado ? 'Copiado!' : 'Copiar'}</button>
            </div>
          </div>
        </>
      ) : (
        <>
          <h1 className="font-display text-3xl font-black text-verde">{pago ? 'Pagamento aprovado! 🎉' : 'Pedido recebido'}</h1>
          <p className="mt-2 text-amber-100/80">
            {pago ? `Seu pagamento em ${r.parcelas}x foi aprovado e o pedido entrou para produção.` : 'Estamos processando o pagamento do seu cartão.'}
          </p>
        </>
      )}
      <p className="mt-6 text-xs text-amber-100/50">Pedido {r.orderId.slice(0, 8)} · {formatBRL(r.valorBrutoCentavos)}</p>
      <button onClick={onNovo} className="mt-6 rounded-full border border-dourado/40 px-5 py-2 text-sm font-bold text-dourado hover:bg-dourado/10">
        Personalizar outra camisa
      </button>
    </div>
  );
}
